import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import 'moment-timezone';

import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { CurrentTimezoneStateService } from 'src/app/shared/current-timezone-state.service';
import { AccountsSelectorComponent } from 'src/app/admin/accounts/accounts-selector/accounts-selector.component';

/**
 * Lightweight shape for an account option shown in the targeting autocomplete.
 */
interface SystemBannerAccountOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-system-banners-edit',
  templateUrl: './system-banners-edit.component.html',
  styleUrls: ['./system-banners-edit.component.scss']
})
export class SystemBannersEditComponent implements OnInit {

  /**
   * True when editing an existing banner, false when creating a new one.
   */
  isEditMode: boolean = false;

  /**
   * ID of the banner being edited (null in create mode).
   */
  bannerId: number | null = null;

  /**
   * Reactive form backing the dialog inputs.
   */
  form: UntypedFormGroup;

  /**
   * In-flight submit flag to prevent duplicate submissions.
   */
  inProgress: boolean = false;

  /**
   * Tracks whether a successful save occurred during this dialog session.
   * Used by onClose() to decide whether to signal the list to refresh.
   */
  wasSaved: boolean = false;

  /**
   * Style options shown in the dropdown.
   */
  styleOptions: Array<{ value: string; label: string }> = [
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error' },
    { value: 'success', label: 'Success' }
  ];

  /**
   * Accounts currently selected as targeting. An empty list means the banner
   * is global (broadcast to all accounts).
   */
  selectedAccounts: SystemBannerAccountOption[] = [];

  /**
   * Moment values bound to the datetime pickers. Kept separate from the
   * reactive form since the picker component emits moment objects directly.
   *
   * The pickers operate in the selected timezone (see `selectedTimezone`),
   * meaning the moment values represent wall-clock time in that zone. They
   * are converted to UTC ISO strings only when sent to the API.
   */
  startsAtDateTime: moment.Moment | null = null;
  endsAtDateTime: moment.Moment | null = null;

  /**
   * Timezone used to interpret the starts_at and ends_at moments. Defaults
   * to the user's current timezone (from CurrentTimezoneStateService) for
   * a new banner; otherwise loaded from the existing banner. Updated by the
   * <app-timezone-select> output.
   */
  selectedTimezone: string;

  /**
   * Tracks whether the schedule range (starts_at < ends_at) is valid.
   * Updated whenever either picker emits a new value.
   */
  scheduleRangeInvalid: boolean = false;

  /**
   * TinyMCE init config. Intentionally restricted to bold, italic, underline
   * and links only. No images, tables, headings or lists are allowed because
   * the server-side sanitiser will strip anything outside the allow list.
   *
   * Paragraphs (<p>) and line breaks (<br>) are permitted so super admins
   * can compose multi-line banner copy. Pressing Enter inserts a paragraph;
   * Shift+Enter inserts a single <br>.
   */
  editorInit: any = {
    base_url: '/assets/tinymce',
    menubar: false,
    plugins: 'link',
    toolbar: 'bold italic underline | link unlink',
    height: 200,
    branding: false,
    statusbar: false,
    license_key: 'gpl',
    valid_elements: 'p,br,b,strong,i,em,u,a[href|target|rel]',
    valid_styles: {},
    extended_valid_elements: '',
    // Force TinyMCE to use <u> for underline instead of inline styles on spans.
    formats: {
      underline: { inline: 'u' }
    },
    // Default link target to _blank so links open in a new tab.
    default_link_target: '_blank',
    link_default_protocol: 'https'
  };

  constructor(
    public dialogRef: MatDialogRef<SystemBannersEditComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private fb: UntypedFormBuilder,
    private api: ApiRequestService,
    private cTmzState: CurrentTimezoneStateService,
    public utils: UtilsService
  ) {
    this.form = this.buildForm();
    // Default timezone for new banners: the super admin's currently
    // selected timezone (falls back to the browser's local zone).
    this.selectedTimezone = this.cTmzState.getCurrentTimezone();
  }

  ngOnInit(): void {
    const banner: any = this.dialogData ? this.dialogData.banner : null;
    if ( banner && banner.id ) {
      this.isEditMode = true;
      this.bannerId = banner.id;
      this.applyBannerToForm(banner);
    }
  }

  /**
   * Build the reactive form with field-level and form-level validators.
   */
  private buildForm(): UntypedFormGroup {
    return this.fb.group(
      {
        content: ['', [Validators.required]],
        style: ['info', [Validators.required]],
        show_web: [true],
        show_mobile: [true],
        show_kiosk: [true],
        is_dismissible: [true],
        include_network: [{ value: false, disabled: true }]
      },
      {
        validators: [this.atLeastOnePlatformValidator()]
      }
    );
  }

  /**
   * Populate form fields from an existing banner record.
   *
   * starts_at / ends_at come back from the API as UTC ISO strings. They are
   * converted to the banner's stored timezone for the picker so the super
   * admin sees the same wall-clock time they originally entered.
   */
  private applyBannerToForm(banner: any): void {
    this.form.patchValue({
      content: banner.content || '',
      style: banner.style || 'info',
      show_web: !!banner.show_web,
      show_mobile: !!banner.show_mobile,
      show_kiosk: !!banner.show_kiosk,
      is_dismissible: !!banner.is_dismissible
    });

    // Use the banner's stored timezone if present, otherwise fall back to
    // whatever default was set in the constructor.
    if ( banner.timezone ) {
      this.selectedTimezone = banner.timezone;
    }

    // Convert UTC timestamps back into the selected zone so the picker
    // displays the original wall-clock time.
    this.startsAtDateTime = banner.starts_at
      ? moment.utc(banner.starts_at).tz(this.selectedTimezone)
      : null;
    this.endsAtDateTime = banner.ends_at
      ? moment.utc(banner.ends_at).tz(this.selectedTimezone)
      : null;

    // Seed selected accounts from the banner payload.
    if ( Array.isArray(banner.accounts) ) {
      this.selectedAccounts = banner.accounts.map((a: any) => ({
        id: Number(a.id),
        name: a.name || ''
      }));
    }

    // Patch include_network and sync the disabled state with account selection.
    this.form.patchValue({ include_network: !!banner.include_network });
    this.syncIncludeNetworkState();
  }

  /**
   * Validator ensuring at least one platform toggle is checked.
   */
  private atLeastOnePlatformValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const web: boolean = !!group.get('show_web')?.value;
      const mobile: boolean = !!group.get('show_mobile')?.value;
      const kiosk: boolean = !!group.get('show_kiosk')?.value;
      return (web || mobile || kiosk) ? null : { platformRequired: true };
    };
  }

  /**
   * Callback from the <app-timezone-select> component. Stores the new
   * timezone string and re-applies it to the existing picker moments so
   * that, e.g., switching from "Pacific/Auckland" to "America/New_York"
   * keeps the wall-clock time the user previously selected (the zone
   * changes the absolute UTC instant the moment refers to).
   */
  getOutputTimezone(timezone: string): void {
    if ( !timezone ) {
      return;
    }
    this.selectedTimezone = timezone;

    // Re-anchor the picker moments to the new zone using their existing
    // wall-clock formatting. This keeps "9:00am on 1 Jun" reading the
    // same after the user changes zones.
    if ( this.startsAtDateTime ) {
      const wallClock: string = this.startsAtDateTime.format('YYYY-MM-DD HH:mm:ss');
      this.startsAtDateTime = moment.tz(wallClock, this.selectedTimezone);
    }
    if ( this.endsAtDateTime ) {
      const wallClock: string = this.endsAtDateTime.format('YYYY-MM-DD HH:mm:ss');
      this.endsAtDateTime = moment.tz(wallClock, this.selectedTimezone);
    }
    this.revalidateScheduleRange();
  }

  /**
   * Callback from the starts_at datetime picker.
   */
  onStartsAtChanged(value: moment.Moment | null): void {
    this.startsAtDateTime = value || null;
    this.revalidateScheduleRange();
  }

  /**
   * Callback from the ends_at datetime picker.
   */
  onEndsAtChanged(value: moment.Moment | null): void {
    this.endsAtDateTime = value || null;
    this.revalidateScheduleRange();
  }

  /**
   * Compute the schedule range validity whenever either picker changes.
   * Only invalid when both values are set and starts_at is not strictly
   * before ends_at.
   */
  private revalidateScheduleRange(): void {
    if ( !this.startsAtDateTime || !this.endsAtDateTime ) {
      this.scheduleRangeInvalid = false;
      return;
    }
    this.scheduleRangeInvalid = !this.startsAtDateTime.isBefore(this.endsAtDateTime);
  }

  /**
   * Open the super admin accounts selector dialog to pick target accounts.
   * Mirrors the pattern used by users-edit when assigning an employer or
   * linking multiple accounts, which is ideal for tenants with thousands of
   * accounts because it supports search, pagination, and sorting.
   */
  onSelectAccounts(): void {
    this.utils.showComponentDialog(
      AccountsSelectorComponent,
      {
        multiple: true,
        returnObjects: true,
        selected: this.selectedAccounts
      },
      {
        disableClose: false,
        width: '1200px'
      },
      (accounts: any[]) => {
        if ( !accounts ) {
          // Dialog closed without a selection; keep the existing list.
          return;
        }
        this.selectedAccounts = (accounts || []).map((a: any) => ({
          id: Number(a.id),
          name: a.name || ''
        }));
        this.syncIncludeNetworkState();
      }
    );
  }

  /**
   * Remove a single account from the selected list.
   */
  onRemoveAccount(account: SystemBannerAccountOption): void {
    this.selectedAccounts = this.selectedAccounts.filter((a) => a.id !== account.id);
    this.syncIncludeNetworkState();
  }

  /**
   * Clear every selected account (revert to a global broadcast).
   */
  onClearAccounts(): void {
    this.selectedAccounts = [];
    this.syncIncludeNetworkState();
  }

  /**
   * Sync the include_network form control's disabled state with the
   * current account selection. When no accounts are selected, the
   * checkbox is disabled and forced to false (Requirement 18.5).
   */
  private syncIncludeNetworkState(): void {
    const ctrl = this.form.get('include_network');
    if ( !ctrl ) {
      return;
    }
    if ( this.selectedAccounts.length === 0 ) {
      ctrl.setValue(false, { emitEvent: false });
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  /**
   * Convenience getter for templates when styling invalid platform group.
   */
  get platformsInvalid(): boolean {
    return !!this.form.errors && !!this.form.errors['platformRequired']
      && (this.form.touched || this.form.dirty);
  }

  /**
   * True when either the mobile or kiosk platform is enabled. Used to show
   * the content real estate warning so super admins know to keep banner
   * copy short on small-screen platforms.
   */
  get showSmallScreenWarning(): boolean {
    return !!this.form.get('show_mobile')?.value
      || !!this.form.get('show_kiosk')?.value;
  }

  /**
   * True when the form cannot be saved right now: any field-level error, an
   * invalid schedule range, or a submit already in flight.
   */
  get cannotSave(): boolean {
    return this.inProgress || this.form.invalid || this.scheduleRangeInvalid;
  }

  /**
   * Save the banner. Creates on first save, updates on subsequent saves.
   */
  onSave(): void {
    if ( this.inProgress ) {
      return;
    }
    if ( this.form.invalid || this.scheduleRangeInvalid ) {
      this.form.markAllAsTouched();
      this.utils.showFormValidationError('Please review the highlighted fields.');
      return;
    }

    const raw: any = this.form.getRawValue();
    const payload: any = {
      content: raw.content,
      style: raw.style,
      show_web: !!raw.show_web,
      show_mobile: !!raw.show_mobile,
      show_kiosk: !!raw.show_kiosk,
      is_dismissible: !!raw.is_dismissible,
      include_network: !!raw.include_network,
      starts_at: this.formatScheduleMoment(this.startsAtDateTime),
      ends_at: this.formatScheduleMoment(this.endsAtDateTime),
      timezone: this.selectedTimezone || null,
      account_ids: this.selectedAccounts.map((a) => a.id)
    };

    this.inProgress = true;

    const request: Promise<any> = this.isEditMode && this.bannerId
      ? this.api.makeRequest('put', `v2/admin/system-banners/${this.bannerId}`, payload)
      : this.api.makeRequest('post', 'v2/admin/system-banners', payload);

    request
      .then((response: any): void => {
        this.utils.showToast('Banner saved.');
        this.wasSaved = true;

        // After a successful create, switch to edit mode so the dialog
        // stays open and the user can continue editing (e.g. scheduling,
        // account targeting). After an update, just stay open.
        if ( !this.isEditMode && response && response.data && response.data.id ) {
          this.isEditMode = true;
          this.bannerId = response.data.id;
        }
      })
      .catch((errorResponse: any): void => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  /**
   * Close the dialog. Signals the list to refresh only when a save
   * actually occurred during this session.
   */
  onClose(): void {
    this.dialogRef.close(this.wasSaved ? true : undefined);
  }

  /**
   * Convert a picker moment to a UTC ISO 8601 string anchored in
   * `selectedTimezone`. Returns null when the moment is not set.
   *
   * The picker emits a moment that represents a wall-clock time without
   * an authoritative zone. We re-parse its formatted value with the
   * super admin's selected timezone, which gives us the correct absolute
   * instant before serializing to UTC for the API.
   *
   * Seconds and milliseconds are zeroed so banners go live exactly on
   * the selected minute. Without this, the moment would carry the seconds
   * from when the form was submitted, delaying visibility by an arbitrary
   * fraction of a minute.
   *
   * The backend explicitly expects an ISO string with a Z suffix so it
   * can disambiguate the timezone before storing. Naive `Y-m-d H:i:s`
   * strings would be reinterpreted by Laravel's datetime cast as wall
   * clock in `app.timezone`, shifting the absolute instant when that
   * timezone is not UTC.
   */
  private formatScheduleMoment(value: moment.Moment | null): string | null {
    if ( !value ) {
      return null;
    }
    const wallClock: string = value.format('YYYY-MM-DD HH:mm:00');
    return moment
      .tz(wallClock, this.selectedTimezone || 'UTC')
      .utc()
      .toISOString();
  }
}
